import React, { FC, useCallback, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CustomCellRendererProps } from "ag-grid-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Switch } from "@axin-org/comet";
import { tw } from "twind";

import { ComponentState } from "types/Component";
import ProcessusLinkRenderer from "./ProcessusLinkRenderer";
import OelForTable from "composants/oel/OelForTable";
import HtmlCellRenderer from "./HtmlCellRenderer";
import { RowHeightContext } from "hooks/useRowMaxHeight";
import { useElementSize } from "hooks/useElementSize";

// Suit la dernière modalité d'interaction de l'utilisateur (souris vs clavier).
// Permet de savoir si l'édition d'une cellule a été déclenchée par un clic ou par
// une navigation clavier (ex. touche Tab), `eventKey` d'AG Grid n'étant pas fiable.
let lastInteractionWasPointer = false;

if (typeof window !== "undefined") {
  const markPointer = () => {
    lastInteractionWasPointer = true;
  };
  const markKeyboard = () => {
    lastInteractionWasPointer = false;
  };
  // `capture: true` pour être notifié avant qu'AG Grid ne démarre l'édition.
  window.addEventListener("mousedown", markPointer, true);
  window.addEventListener("pointerdown", markPointer, true);
  window.addEventListener("touchstart", markPointer, true);
  window.addEventListener("keydown", markKeyboard, true);
}

interface OelCellRendererProps {
  colId: string;
  fieldName: string;
  value: any;
  rowIndex: number;
  options?: Record<string, any>;
}

const OelCellRenderer: FC<OelCellRendererProps> = ({
  colId,
  fieldName,
  value,
  rowIndex,
  options,
}) => {
  const registerHeight = useContext(RowHeightContext);
  const { ref: sizeRef, height: contentHeight } = useElementSize<HTMLDivElement>();
  const divRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      divRef.current = el;
      sizeRef.current = el;
    },
    [sizeRef],
  );

  useEffect(() => {
    if (registerHeight && contentHeight > 0) {
      registerHeight(rowIndex, contentHeight);
    }
  }, [contentHeight, rowIndex, registerHeight]);

  return (
    <div
      ref={setRef}
      col-id={colId + "-html"}
      className={tw("flex items-center justify-center h-full")}
    >
      <OelForTable name={fieldName} value={value} {...options} />
    </div>
  );
};

export function buildOelRenderer(compo: ComponentState) {
  return (props: any) => {
    return (
      <OelCellRenderer
        colId={compo.column}
        fieldName={props.column.field}
        value={props.value}
        rowIndex={props.node?.rowIndex ?? 0}
        options={compo.options}
      />
    );
  };
}

function isCellEditable(props: { colDef?: { editable?: unknown }; data?: unknown }) {
  const editable = props.colDef?.editable;
  if (typeof editable === "function") {
    return !!editable(props);
  }
  return editable === true;
}

export function buildHtmlRenderer(compo: ComponentState) {
  return (props: any) => {
    return (
      <HtmlCellRenderer
        value={props.value}
        colId={compo.column}
        rowIndex={props.node?.rowIndex ?? 0}
        editable={isCellEditable(props)}
        title={compo.label}
      />
    );
  };
}

export function buildLinkRenderer(compo: ComponentState) {
  return (props: any) => {
    return (
      <Link
        to={compo.options?.to + "/" + encodeURIComponent(props.value)}
        title={props.value}
        className={tw("no-underline text-blue-500 hover:text-blue-700")}
      >
        {props.value}
      </Link>
    );
  };
}

export const CheckBoxRenderer: FC<Partial<CustomCellRendererProps>> = (props) => {
  if (props.value === undefined || props.value === null) {
    return <div />;
  }
  return (
    <div>
      <Switch
        id={`${props.data?.id}-${props.colDef?.field}`}
        name={props.colDef?.field}
        disabled={props.colDef?.editable === false}
        checked={props.value === true || props.value === "O" || props.value === "true"}
        data-value={props.value}
        onCheckedChange={(val) => {}}
        onContextMenu={() => {}}
      />
    </div>
  );
};

export function CheckBoxCellEditor(compo: ComponentState) {
  return (props: any) => {
    const { value, onValueChange, data, stopEditing, cellStartedEdit } = props;

    useEffect(() => {
      // On ne bascule la valeur que si l'édition a démarré suite à un clic souris,
      // et non lors d'une navigation au clavier (ex. touche Tab).
      if (cellStartedEdit && lastInteractionWasPointer) {
        const initialValue = props.value === true || props.value === "O" || props.value === "true";
        onValueChange(!initialValue);
      }
    }, [cellStartedEdit]);

    return (
      <div className={tw("px-[15px]")}>
        <Switch
          id={`edit-${compo.column}-${data.id}`}
          name={compo.column}
          checked={props.value === true || props.value === "O" || props.value === "true"}
          onCheckedChange={(val) => {
            onValueChange(val);
          }}
        />
      </div>
    );
  };
}

export const CheckboxFilterRenderer: FC<any> = (props) => {
  const { t } = useTranslation();
  if (props.value === "(Sélectionner tout)") {
    return <span>{t("commun_selectionner_tout")}</span>;
  }
  return (
    <CheckBoxRenderer
      value={props.value === "true"}
      colDef={props.colDef}
      data={{ id: `filter-${props.value}` }}
    />
  );
};

export function buildBooleanCellRenderer(compo: ComponentState) {
  return (props: Partial<CustomCellRendererProps>) => {
    if (!props.value) {
      return <div />;
    }
    const [t] = useTranslation();

    let booleanValue: boolean | null = null;
    const value = props.value;
    let traductionKey = "";
    if (typeof value == "string") {
      if (value === "O") {
        traductionKey = "commun_oui";
        booleanValue = true;
      } else if (value === "N") {
        traductionKey = "commun_non";
        booleanValue = false;
      }
    } else if (typeof value == "boolean") {
      if (value === true) {
        traductionKey = "commun_oui";
        booleanValue = true;
      } else if (value === false) {
        traductionKey = "commun_non";
        booleanValue = false;
      }
    }

    let baseTailwind =
      "inline-block min-w-[3em] rounded px-2 py-1 text-center text-xs font-semibold";
    let statusTailwind =
      booleanValue === null
        ? "bg-gray-200 text-gray-600"
        : booleanValue === true
          ? "bg-green-100 text-green-800"
          : "bg-blue-100 text-blue-800";
    let className = classNames(tw(baseTailwind), tw(statusTailwind), compo.options?.className);

    const translatedValue = t(traductionKey, { defaultValue: value });

    return (
      <span {...props} className={className} style={{ minWidth: "3em", ...compo.options?.style }}>
        {translatedValue}
      </span>
    );
  };
}

export function buildTagRenderer(compo: ComponentState) {
  return (props: any) => {
    if (!props.value) {
      return <div />;
    }
    return (
      <div className={tw("w-full")}>
        <span
          className={classNames("tag", compo.options?.className)}
          style={{ paddingLeft: "1rem", paddingRight: "1rem" }}
        >
          {props.value}
        </span>
      </div>
    );
  };
}

export const SysDomaineIRRenderer: FC<
  Partial<CustomCellRendererProps> & { compo: ComponentState }
> = (props) => {
  const { t } = useTranslation("sd");
  const rsckey = props.data[props.compo.options?.labelColumn];
  return <span {...props}>{t(rsckey) ?? props.value}</span>;
};

export function buildSysDomaineIRRenderer(compo: ComponentState) {
  return (props: any) => <SysDomaineIRRenderer compo={compo} {...props} />;
}

export function buildProcessusLinkRenderer(compo: ComponentState, onAfterSaveProcess?: () => void) {
  return (props: any) => {
    return (
      <ProcessusLinkRenderer
        definition={compo}
        {...props}
        onAfterSaveProcess={onAfterSaveProcess}
      />
    );
  };
}
